import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { GO_TO_GROUP_PARAM, REVIEW_ROUTE_SEGMENT, stepIndexForGroup } from '../briefNavigation';

/**
 * Resolves a `?goToGroup=<id>` deep link to the wizard's numeric `?step=` once the
 * groups are known, then rewrites the URL (replace, dropping `goToGroup`).
 *
 * An unresolvable id falls back to the review page (a sibling route), which
 * summarises the whole brief, rather than step 1.
 *
 * @param orderedGroups wizard groups in display order (`[intro, ...groups]`)
 * @param ready whether `orderedGroups` is final (criteria/flags loaded)
 * @returns true while resolution is pending, so callers can hold rendering and
 *          avoid flashing step 1.
 */
export function useResolveGroupDeepLink(
    orderedGroups: ReadonlyArray<{ id: string }>,
    ready: boolean
): boolean {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const groupId = searchParams.get(GO_TO_GROUP_PARAM);
    const hasStepParam = searchParams.get('step') != null;
    // Pending only when a step still needs resolving; an existing step just gets
    // its redundant goToGroup stripped below.
    const isPending = Boolean(groupId) && !hasStepParam;

    useEffect(() => {
        if (!groupId) return;

        // An explicit step wins; drop the redundant goToGroup.
        if (hasStepParam) {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete(GO_TO_GROUP_PARAM);
                    return next;
                },
                { replace: true }
            );
            return;
        }

        if (!ready) return;

        const step = stepIndexForGroup(orderedGroups, groupId);

        // Unknown group id: fall back to the review route (replace, dropping goToGroup).
        if (step === null) {
            navigate(`${pathname.replace(/\/$/, '')}/${REVIEW_ROUTE_SEGMENT}`, { replace: true });
            return;
        }

        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('step', String(step));
                next.delete(GO_TO_GROUP_PARAM);
                return next;
            },
            // Replace so the deep link isn't a back-button trap.
            { replace: true }
        );
    }, [hasStepParam, ready, groupId, orderedGroups, setSearchParams, navigate, pathname]);

    return isPending;
}
